import { useRef, useState, useEffect, useCallback, type ReactNode } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface TruncatedTooltipProps {
  children: ReactNode;
  className?: string;
}

export const TruncatedTooltip = ({ children, className }: TruncatedTooltipProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = useCallback(() => {
    const el = ref.current;
    if (!el) return;

    const overflowing = el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth;
    setIsTruncated(overflowing);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    checkTruncation();

    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkTruncation, children]);

  if (!isTruncated) {
    return (
      <span ref={ref} className={`block truncate ${className ?? ""}`}>
        {children}
      </span>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span ref={ref} className={`block truncate ${className ?? ""}`}>
          {children}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <p className="break-all whitespace-pre-line">{children}</p>
      </TooltipContent>
    </Tooltip>
  );
};
